import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useFetch, usePost, notify } from '../../../../components/component-items/helpers'
import { globalState } from '../../../../store'
import { usePermissions } from '../../../../hooks/usePermissions'

// Components
import CardLoading from '../../../../components/component-items/loading-popover'
import GlobalStyles from '../../../../components/component-items/styles'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-duotone-svg-icons'

// RBAC Components
import { RoleAssignmentTable } from './RoleAssignmentTable'
import { EffectivePermissionsMatrix } from './EffectivePermissionsMatrix'
import { OverridesTable } from './OverridesTable'
import { AssignRoleModal } from './AssignRoleModal'
import { OverrideModal } from './OverrideModal'
import { UserAuditHistoryCard } from '../../../../components/users'

// Types
import type {
  WarehouseUserRoleAssignment,
  WarehouseUserOverride,
  WarehouseEffectivePermissions,
  RoleListItem,
  WarehouseOption,
} from '../../../../types/rbac'

interface UserInfo {
  id: number
  first_name: string
  last_name: string
  email: string
  username: string
}

export const UserPermissionPage = () => {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const {
    state: { csrf, warehouseActive, activeWarehouses, warehousesInactive, warehousesRemaining },
  } = globalState()

  // Modal states
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false)
  const [showOverrideModal, setShowOverrideModal] = useState(false)
  const [editingRoleAssignment, setEditingRoleAssignment] = useState<WarehouseUserRoleAssignment | null>(null)
  const [editingOverride, setEditingOverride] = useState<WarehouseUserOverride | null>(null)

  // Data refresh trigger
  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey((k) => k + 1)

  // Fetch user info
  const { response: userResp, loaded: userLoaded }: any = useFetch(
    `/api/warehouse/${warehouseActive?.id}/users/${userId}/`,
    {},
    undefined,
    true
  )
  const userInfo: UserInfo | null = userLoaded ? userResp : null

  // Fetch role assignments
  const { response: rolesResp, loaded: rolesLoaded }: any = useFetch(
    `/api/rbac/users/${userId}/roles/?refresh=${refreshKey}`,
    {},
    undefined,
    true
  )
  const roleAssignments: WarehouseUserRoleAssignment[] = rolesLoaded ? rolesResp?.data || [] : []

  // Fetch overrides
  const { response: overridesResp, loaded: overridesLoaded }: any = useFetch(
    `/api/rbac/users/${userId}/overrides/?refresh=${refreshKey}`,
    {},
    undefined,
    true
  )
  const overrides: WarehouseUserOverride[] = overridesLoaded ? overridesResp?.data || [] : []

  // Fetch effective permissions
  const { response: effectiveResp, loaded: effectiveLoaded }: any = useFetch(
    `/api/rbac/users/${userId}/permissions/effective/?refresh=${refreshKey}`,
    {},
    undefined,
    true
  )
  const effectivePermissions: WarehouseEffectivePermissions | null = effectiveLoaded ? effectiveResp?.data : null

  // Fetch available roles
  const { response: availableRolesResp, loaded: availableRolesLoaded }: any = useFetch(
    `/api/rbac/roles/?user_type=warehouse`,
    {},
    undefined,
    true
  )
  const availableRoles: RoleListItem[] = availableRolesLoaded ? availableRolesResp?.results || [] : []

  // Get warehouses from store - combine all available warehouse lists
  const allWarehouses = [
    ...(warehouseActive ? [warehouseActive] : []),
    ...(warehousesInactive || []),
    ...(warehousesRemaining || []),
    ...(activeWarehouses || []),
  ]
  // Dedupe by id
  const warehouseMap = new Map<number, WarehouseOption>()
  allWarehouses.forEach((w: any) => {
    if (w?.id && !warehouseMap.has(w.id)) {
      warehouseMap.set(w.id, { id: w.id, name: w.name })
    }
  })
  const warehouses: WarehouseOption[] = Array.from(warehouseMap.values())

  const allLoaded = userLoaded && rolesLoaded && overridesLoaded && effectiveLoaded

  // Permission check: managing user permissions requires settings:operator+
  const { hasAccess } = usePermissions()
  const canManagePermissions = hasAccess('settings', 'operator')

  // Handlers
  const handleAssignRole = () => {
    setEditingRoleAssignment(null)
    setShowAssignRoleModal(true)
  }

  const handleEditRoleAssignment = (assignment: WarehouseUserRoleAssignment) => {
    setEditingRoleAssignment(assignment)
    setShowAssignRoleModal(true)
  }

  const handleRemoveRoleAssignment = async (assignmentId: number) => {
    notify({
      type: 'warning',
      message: 'Are you sure you want to remove this role assignment?',
      requestConfirmation: true,
      onSubmit: async () => {
        const resp = await fetch(`/api/rbac/users/${userId}/roles/${assignmentId}/`, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrf,
          },
        })
        if (resp.ok) {
          notify({ type: 'success', message: 'Role assignment removed' })
          refresh()
        } else {
          notify({ type: 'error', message: 'Failed to remove role assignment' })
        }
      },
      autoClose: false,
    })
  }

  const handleAddOverride = () => {
    setEditingOverride(null)
    setShowOverrideModal(true)
  }

  const handleEditOverride = (override: WarehouseUserOverride) => {
    setEditingOverride(override)
    setShowOverrideModal(true)
  }

  const handleRemoveOverride = async (override: WarehouseUserOverride) => {
    notify({
      type: 'warning',
      message: 'Are you sure you want to remove this override?',
      requestConfirmation: true,
      onSubmit: async () => {
        const resp = await usePost(
          `/api/rbac/users/${userId}/overrides/bulk/`,
          {
            operations: [
              {
                segment: override.segment,
                level: null, // null = delete
                warehouse_id: override.warehouse,
              },
            ],
          },
          csrf,
          false,
          true,
          true,
          true,
          false
        )
        if (resp?.data) {
          notify({ type: 'success', message: 'Override removed' })
          refresh()
        } else {
          notify({ type: 'error', message: 'Failed to remove override' })
        }
      },
      autoClose: false,
    })
  }

  const handleRoleSaved = () => {
    setShowAssignRoleModal(false)
    refresh()
  }

  const handleOverrideSaved = () => {
    setShowOverrideModal(false)
    refresh()
  }

  if (!allLoaded) {
    return (
      <GlobalStyles.FullPageCard>
        <CardLoading text="Loading permissions..." error={false} />
      </GlobalStyles.FullPageCard>
    )
  }

  return (
    <GlobalStyles.FullPageCard>
      <GlobalStyles.CardHeader>
        <GlobalStyles.CardTitle>
          <h3>
            User Permissions
            <small>
              {userInfo?.first_name} {userInfo?.last_name} ({userInfo?.username})
            </small>
          </h3>
        </GlobalStyles.CardTitle>
        <GlobalStyles.CardToolbar>
          <GlobalStyles.Button className="secondary" onClick={() => navigate('/warehouse/users/')}>
            <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: '0.5em' }} />
            Back to Users
          </GlobalStyles.Button>
        </GlobalStyles.CardToolbar>
      </GlobalStyles.CardHeader>

      <div style={{ padding: '1em 2em' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
          {/* Left Column - Main Content (65%) */}
          <div style={{ flex: '2', minWidth: '500px' }}>
            {/* Section: Assigned Roles */}
            <section style={{ marginBottom: '2em' }}>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}
              >
                <h4 style={{ margin: 0 }}>Assigned Roles</h4>
                {canManagePermissions && (
                  <GlobalStyles.Button onClick={handleAssignRole}>+ Assign Role</GlobalStyles.Button>
                )}
              </div>
              <RoleAssignmentTable
                assignments={roleAssignments}
                onEdit={handleEditRoleAssignment}
                onRemove={handleRemoveRoleAssignment}
              />
            </section>

            {/* Section: Effective Permissions */}
            <section style={{ marginBottom: '2em' }}>
              <h4 style={{ marginBottom: '1em' }}>Effective Permissions (Read Only)</h4>
              <p style={{ color: '#6c7293', fontSize: '0.9em', marginBottom: '1em' }}>
                This matrix shows the resolved permission level for each segment across all scopes. Cells marked with *
                have an override applied.
              </p>
              {effectivePermissions && <EffectivePermissionsMatrix permissions={effectivePermissions} />}
            </section>

            {/* Section: Overrides */}
            <section>
              <div
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}
              >
                <h4 style={{ margin: 0 }}>Overrides (Exceptions)</h4>
                {canManagePermissions && (
                  <GlobalStyles.Button onClick={handleAddOverride}>+ Add Override</GlobalStyles.Button>
                )}
              </div>
              <p style={{ color: '#6c7293', fontSize: '0.9em', marginBottom: '1em' }}>
                Overrides allow you to grant or restrict specific permissions beyond what the assigned role provides.
              </p>
              <OverridesTable overrides={overrides} onEdit={handleEditOverride} onRemove={handleRemoveOverride} />
            </section>
          </div>

          {/* Right Column - Permission History (35%) */}
          <div style={{ flex: '1', minWidth: '300px', maxWidth: '400px', flexBasis: '300px' }}>
            <UserAuditHistoryCard userId={Number(userId)} limit={10} />
          </div>
        </div>
      </div>

      {/* Modals */}
      <AssignRoleModal
        show={showAssignRoleModal}
        onHide={() => setShowAssignRoleModal(false)}
        onSaved={handleRoleSaved}
        userId={Number(userId)}
        existingAssignment={editingRoleAssignment}
        availableRoles={availableRoles}
        warehouses={warehouses}
        currentWarehouseId={warehouseActive?.id || null}
        csrf={csrf}
      />

      <OverrideModal
        show={showOverrideModal}
        onHide={() => setShowOverrideModal(false)}
        onSaved={handleOverrideSaved}
        userId={Number(userId)}
        existingOverride={editingOverride}
        warehouses={warehouses}
        currentWarehouseId={warehouseActive?.id || null}
        csrf={csrf}
      />
    </GlobalStyles.FullPageCard>
  )
}

export default UserPermissionPage
